/** The locales the public site publishes its legal pages in — the three the dashboard ships. */
export type LegalLocale = "en" | "es" | "pt";

export interface LegalLinks {
  terms: string;
  privacy: string;
}

/**
 * Public marketing-site legal pages, per locale.
 *
 * Not an env var: there is no staging copy of a legal document, and `ConfigService` is for values that differ
 * per environment. It lives in `shared/` rather than in the registration module because the verification flow
 * — which is served to people who have no account and never see a sign-up form — links to them too.
 */
export const LEGAL_LINKS: Record<LegalLocale, LegalLinks> = {
  en: {
    terms: "https://colocapayments.com/en/terminos",
    privacy: "https://colocapayments.com/en/politica-privacidad",
  },
  es: {
    terms: "https://colocapayments.com/es/terminos",
    privacy: "https://colocapayments.com/es/politica-privacidad",
  },
  pt: {
    terms: "https://colocapayments.com/pt/terminos",
    privacy: "https://colocapayments.com/pt/politica-privacidad",
  },
};

function isLegalLocale(value: string): value is LegalLocale {
  return value === "en" || value === "es" || value === "pt";
}

/**
 * The legal pages for the language on screen.
 *
 * Takes the raw active language because that is what `useTranslations` hands back — `i18n.language`, which
 * can carry a region (`es-CO`) — and falls back to `es`: i18next's own `fallbackLng`, and the language the
 * documents are authoritative in, as the English page states itself.
 */
export function legalLinksFor(language: string): LegalLinks {
  const locale = language.slice(0, 2).toLowerCase();
  return isLegalLocale(locale) ? LEGAL_LINKS[locale] : LEGAL_LINKS.es;
}
